import React, { useState, useEffect, useCallback } from 'react';
import { stokV2Ara } from '../services/stokV2Service';
import StokParcaSiparisGecmisiModal from '../components/stok/StokParcaSiparisGecmisiModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const adetStyle = (adet) => {
  if (adet < 0) return { textAlign: 'right', fontWeight: 600, color: 'var(--danger, #e74c3c)' };
  if (adet === 0) return { textAlign: 'right', fontWeight: 600, color: 'var(--text-light, #999)' };
  return { textAlign: 'right', fontWeight: 600 };
};

const renderKatmanlarAltSatir = (katmanlar) => {
  if (!katmanlar?.length) return null;
  return (
    <div style={{ fontSize: '11px', color: 'var(--text-gray)', marginTop: '4px', lineHeight: 1.45 }}>
      {katmanlar.map((k, i) => (
        <span key={k.stokKatmanId || i} style={{ display: 'inline-block', marginRight: '8px' }}>
          {Number(k.birimMaliyet).toFixed(2)}₺×{k.toplam}
          {(k.rezerveAdet || 0) > 0 ? ` (${k.rezerveAdet} rez.)` : ''}
        </span>
      ))}
    </div>
  );
};

const StokAraPage = ({ embedded = false } = {}) => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sonuclar, setSonuclar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [sayfalama, setSayfalama] = useState({ toplam: 0, toplamSayfa: 1, mevcutSayfa: 1, limit: PAGE_DEFAULT_LIMIT });
  const [gecmisParca, setGecmisParca] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setSayfa(1);
  }, [debouncedQ]);

  const load = useCallback(async () => {
    if (!debouncedQ) {
      setSonuclar([]);
      setSayfalama({ toplam: 0, toplamSayfa: 1, mevcutSayfa: 1, limit: PAGE_DEFAULT_LIMIT });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await stokV2Ara({ q: debouncedQ, sayfa, limit: PAGE_DEFAULT_LIMIT });
      if (response?.basarili && response?.veri) {
        setSonuclar(response.veri.sonuclar || []);
        setSayfalama(response.veri.sayfalama || { toplam: 0, toplamSayfa: 1, mevcutSayfa: sayfa, limit: PAGE_DEFAULT_LIMIT });
      } else {
        setSonuclar([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Arama yapılamadı'));
      setSonuclar([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, sayfa]);

  useEffect(() => {
    load();
  }, [load]);

  const Wrapper = embedded ? 'div' : 'main';
  const wrapperClass = embedded ? 'stok-ara-embedded' : 'main-content stok-ara-page';

  return (
    <Wrapper className={wrapperClass}>
      {!embedded && (
        <div className="page-header">
          <h1>Stok Ara</h1>
        </div>
      )}
      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ maxWidth: '400px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Parça adı veya kodu yazın..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {loading ? (
        <p>Aranıyor...</p>
      ) : debouncedQ && sonuclar.length === 0 ? (
        <p>Sonuç bulunamadı.</p>
      ) : debouncedQ ? (
        <>
          <div className="table-responsive">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Parça adı</th>
                  <th>Parça kodu</th>
                  <th>Teknik servis</th>
                  <th style={{ textAlign: 'right', width: '100px' }}>Adet</th>
                </tr>
              </thead>
              <tbody>
                {sonuclar.map((row, idx) => (
                  <tr
                    key={`${row.teknikServisId}-${row.icerikId}-${idx}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setGecmisParca({
                        teknikServisId: row.teknikServisId,
                        icerikId: row.icerikId,
                        parcaAdi: row.parcaAdi,
                        parcaKodu: row.parcaKodu,
                      })
                    }
                    title="Sipariş geçmişini görmek için tıklayın"
                  >
                    <td>
                      <div>{row.parcaAdi || '—'}</div>
                      {renderKatmanlarAltSatir(row.katmanlar)}
                    </td>
                    <td>{row.parcaKodu || '—'}</td>
                    <td>{row.teknikServisAdi || '—'}</td>
                    <td style={adetStyle(row.adet ?? 0)}>{row.adet ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={sayfalama.toplam ?? 0}
            currentPage={sayfalama.mevcutSayfa || sayfa}
            totalPages={Math.max(1, sayfalama.toplamSayfa || 1)}
            onPageChange={setSayfa}
            loading={loading}
            embedded
          />
        </>
      ) : null}

      <StokParcaSiparisGecmisiModal
        open={!!gecmisParca}
        onClose={() => setGecmisParca(null)}
        mode="admin"
        teknikServisId={gecmisParca?.teknikServisId}
        icerikId={gecmisParca?.icerikId}
        parcaAdi={gecmisParca?.parcaAdi}
        parcaKodu={gecmisParca?.parcaKodu}
      />
    </Wrapper>
  );
};

export default StokAraPage;
